import CheckIcon from "@mui/icons-material/Check";
import { Box, Checkbox } from "@mui/material";
import { alpha } from "@mui/material/styles";
import { type Control, Controller } from "react-hook-form";

import { Text } from "../../redesign/components/Text";
import { TextInput } from "../../redesign/components/TextInput";
import { type InvitationFormData } from "../useWorkerInvitationForm";

type RowGridTemplateColumns =
  | string
  | {
      xs: string;
      md: string;
    };

interface WorkerInvitationStaffMemberRowProps {
  control: Control<InvitationFormData>;
  index: number;
  isPrnWorkersInvitationRolloutEnabled: boolean;
  rowGridTemplateColumns: RowGridTemplateColumns;
  rowId: string;
  shouldShowFieldError: (index: number, field: "name" | "phone" | "isPrnWorker") => boolean;
}

export function WorkerInvitationStaffMemberRow(props: WorkerInvitationStaffMemberRowProps) {
  const {
    control,
    index,
    isPrnWorkersInvitationRolloutEnabled,
    rowGridTemplateColumns,
    rowId,
    shouldShowFieldError,
  } = props;

  return (
    <Box
      key={rowId}
      sx={(theme) => ({
        display: "grid",
        gridTemplateColumns: rowGridTemplateColumns,
        gap: theme.spacing(3),
        alignItems: "center",
        marginBottom: 3,
      })}
    >
      <Controller
        name={`staffMembers.${index}.name`}
        control={control}
        render={({ field: nameField }) => (
          <TextInput
            {...nameField}
            fullWidth
            size="small"
            placeholder="Enter full name"
            error={shouldShowFieldError(index, "name")}
            inputProps={{
              "aria-label": `Name field for row ${index + 1}`,
            }}
            sx={{
              minWidth: 0,
            }}
            onChange={(event) => {
              nameField.onChange(event.target.value);
            }}
          />
        )}
      />
      <Controller
        name={`staffMembers.${index}.phone`}
        control={control}
        render={({ field: phoneField }) => (
          <TextInput
            {...phoneField}
            fullWidth
            size="small"
            placeholder="(555) 123-4567"
            inputProps={{
              "aria-label": `Phone field for row ${index + 1}`,
              inputMode: "tel",
            }}
            error={shouldShowFieldError(index, "phone")}
            sx={{
              minWidth: 0,
            }}
            onChange={(event) => {
              const value = event.target.value.replaceAll(/\D/g, "");
              if (value.length <= 10) {
                phoneField.onChange(value);
              }
            }}
          />
        )}
      />
      {isPrnWorkersInvitationRolloutEnabled && (
        <Controller
          name={`staffMembers.${index}.isPrnWorker`}
          control={control}
          render={({ field: isPrnWorkerField }) => (
            <Box
              sx={(theme) => ({
                display: "flex",
                alignItems: "center",
                justifyContent: { xs: "flex-start", md: "center" },
                minHeight: "2.625rem",
                [theme.breakpoints.down("md")]: {
                  gap: theme.spacing(2),
                },
              })}
            >
              <Checkbox
                disableRipple
                checked={Boolean(isPrnWorkerField.value)}
                checkedIcon={<CheckedCheckboxIcon />}
                icon={<UncheckedCheckboxIcon />}
                inputProps={{
                  "aria-label": `Is PRN worker field for row ${index + 1}`,
                }}
                sx={(theme) => ({
                  padding: 0,
                  color: theme.palette.border?.strong,
                  "&:hover": {
                    backgroundColor: "transparent",
                  },
                  "&.Mui-focusVisible": {
                    borderRadius: theme.borderRadius?.small,
                    boxShadow: `0 0 0 4px ${alpha(theme.palette.primary.main, 0.2)}`,
                  },
                })}
                onChange={(event) => {
                  isPrnWorkerField.onChange(event.target.checked);
                }}
              />
              <Text
                variant="caption"
                sx={(theme) => ({
                  display: { xs: "block", md: "none" },
                  color: theme.palette.text.secondary,
                })}
              >
                PRN worker
              </Text>
            </Box>
          )}
        />
      )}
    </Box>
  );
}

function UncheckedCheckboxIcon() {
  return (
    <Box
      sx={(theme) => ({
        width: "1.25rem",
        height: "1.25rem",
        borderRadius: theme.borderRadius?.small,
        border: `${theme.borderWidth?.thick ?? "2px"} solid ${
          theme.palette.border?.strong ?? theme.palette.action.disabled
        }`,
        backgroundColor: theme.palette.background.standout ?? theme.palette.background.paper,
        boxSizing: "border-box",
      })}
    />
  );
}

function CheckedCheckboxIcon() {
  return (
    <Box
      sx={(theme) => ({
        width: "1.25rem",
        height: "1.25rem",
        borderRadius: theme.borderRadius?.small,
        border: `${theme.borderWidth?.thick ?? "2px"} solid ${theme.palette.primary.main}`,
        backgroundColor: theme.palette.primary.main,
        color: theme.palette.common.white,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        boxSizing: "border-box",
      })}
    >
      <CheckIcon sx={{ fontSize: "0.875rem" }} />
    </Box>
  );
}
